import React, { Ref, useRef } from "react";
import mapboxgl, {
  GeoJSONSource,
  LngLat,
  LngLatBounds,
  MapLayerMouseEvent,
} from "mapbox-gl";
import {
  Map as ReactMap,
  ScaleControl,
  MapRef,
  ViewStateChangeEvent,
  Source,
  Layer,
  GeolocateControl,
  NavigationControl,
  FullscreenControl,
  Marker,
  AttributionControl,
} from "react-map-gl/mapbox";

import { useNavigate, useMatch, NavigateFunction } from "react-router";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import { BooleanParam, StringParam, useQueryParam } from "use-query-params";
import type { FeatureCollection, Point } from "geojson";
import { debounce } from "lodash";

import SportsLocationOverlayLayer from "../SvgOverlay/SportsLocationOverlayLayer";

import GroupToggle from "../GroupToggle/GroupToggle";
import MapRecenter from "../MapRecenter/MapRecenter";
import ApiConfig from "../../services/apiConfiguration";
import { SportsLocation } from "../../services/SportsLocation/SportsLocation.service";
import { LatLngZoom, parseLatLngZoomParam } from "../../utils/utils";
import pkg from "../../../package.json";
import "mapbox-gl/dist/mapbox-gl.css";
import SmartracksLocationInfo from "../SmartracksLocationInfo/SmartracksLocationInfo";

import {
  TRACKS_LAYER_ID,
  tracksClusterLayer,
  tracksUnclusteredPointsLayer,
} from "./layers";

import MapboxWorker from "mapbox-gl/dist/mapbox-gl-csp-worker?worker";
(mapboxgl as any).workerClass = MapboxWorker;

const MAPBOX_TOKEN = ApiConfig.getMapboxToken();
// Careful: MIN_SVG_ZOOM_LEVEL needs to be an integer, because it affects clustering which in turn are only evaluated at integer zoom levels
const MIN_SVG_ZOOM_LEVEL = 13;
const ATTRIBUTION = `v${pkg.version} - ${new Date().getFullYear()} &copy; <a href="https://humotion.net" target="blank">Humotion</a> | <a href="https://humotion.net/en/impressum-2/" target="blank">Legal Notice</a> | <a href="https://humotion.net/en/privacy-policy/" target="blank">Data Privacy</a>`;

type MapProps = {
  mapRef: React.RefObject<MapRef | null>;
  mapStyle?: string | null;
  sportsLocations?: SportsLocation[];
  handleViewLocation: (location: SportsLocation | undefined) => Promise<void>;
  onRegionSelected?: (regionFilter: string | number[] | undefined) => void;
};
export const defaultLatLngZoom: LatLngZoom = {
  latitude: 17,
  longitude: 4,
  zoom: 1.5,
};

const Map = (props: MapProps) => {
  const theme = useTheme();
  const {
    mapRef,
    sportsLocations,
    mapStyle,
    handleViewLocation,
    onRegionSelected,
  } = props;
  const navigate = useNavigate();
  const latLngZoomURLParam = parseLatLngZoomParam(
    useMatch("*")?.params["*"]?.substring(1) ?? "",
    defaultLatLngZoom,
  );
  const [groupPins = true, setGroupPins] = useQueryParam(
    "grouped",
    BooleanParam,
  );

  const geolocateRef = useRef<mapboxgl.GeolocateControl>(null);
  const [autoLocate = false] = useQueryParam("autolocate", BooleanParam);

  const [smartracksLocationName = undefined, setSmartracksLocationName] =
    useQueryParam("facility", StringParam);

  const [latLngZoom, setLatLngZoom] = React.useState<LatLngZoom>(
    latLngZoomURLParam.zoom
      ? {
          latitude: latLngZoomURLParam.latitude,
          longitude: latLngZoomURLParam.longitude,
          zoom: latLngZoomURLParam.zoom,
        }
      : defaultLatLngZoom,
  );

  const [mapLoaded, setMapLoaded] = React.useState(false);
  const [bounds, setBounds] = React.useState<LngLatBounds | null>(null);

  const [hoveredFeatureId, setHoveredFeatureId] = React.useState<
    string | number | undefined
  >(undefined);

  /**
   * Changes in the position shown by the map should be represented in the current URL.
   * This needs to be debounced to not overwhelm the browser with too many updates.
   * Using React.useMemo ensures that the callback is persistent between renders
   * or otherwise debouncing would be useless.
   */
  const debouncedNavigate = React.useMemo(
    () =>
      debounce((url: string, nav: NavigateFunction) => {
        nav(url + window.location.search, { replace: true });
      }, 500),
    [],
  );

  React.useEffect(() => {
    if (
      latLngZoom.latitude === defaultLatLngZoom.latitude &&
      latLngZoom.longitude === defaultLatLngZoom.longitude &&
      latLngZoom.zoom === defaultLatLngZoom.zoom
    )
      debouncedNavigate("/", navigate);
    else {
      const url = `/@${latLngZoom.latitude.toFixed(
        7,
      )},${latLngZoom.longitude.toFixed(7)},${latLngZoom.zoom.toFixed(2)}z`;
      debouncedNavigate(url, navigate);
    }
  }, [latLngZoom, navigate, debouncedNavigate]);

  React.useEffect(() => {
    if (!autoLocate || !mapLoaded) return;
    if (geolocateRef.current?._supportsGeolocation)
      geolocateRef.current.trigger();
  }, [autoLocate, mapLoaded]);

  /**
   * Zooming in and out changes which locations are visible to the user
   * and therefore what images are overlaid. This needs to be updated
   * for every change of the zoom level or the lat/lng.
   */
  const visibleLocations = React.useMemo(() => {
    let locationsInBounds = [] as SportsLocation[];
    if (sportsLocations && bounds && latLngZoom.zoom >= MIN_SVG_ZOOM_LEVEL) {
      // Only checking if the current viewport (aka map bounds) contain the anchor location
      // is not sufficient, because zooming in on a certain part of the track
      // may result in the anchor location being outside of the viewport and thus
      // the sportslocation being hidden.
      // The track should be shown if the viewport either contains the anchor location
      // or if the viewport center is less than a threshold away from the anchor location.
      locationsInBounds = sportsLocations.filter((l) => {
        const anchorLngLat = new LngLat(
          l.anchorLocation.longitude,
          l.anchorLocation.latitude,
        );
        return (
          bounds.contains(anchorLngLat) ||
          bounds.getCenter().distanceTo(anchorLngLat) < 500
        );
      });
    }
    return locationsInBounds;
  }, [sportsLocations, bounds, latLngZoom.zoom]);

  /**
   * Since the short name of a location is used to represent it being
   * selected (aka popup opened) in the URL, we need to crawl
   * the list of all sports locations when the URL changes.
   * With a growing number of tracks this is an expensive operation,
   * hence the usage of React.useMemo
   */
  const LocationInfoData = React.useMemo(() => {
    if (sportsLocations && smartracksLocationName) {
      return sportsLocations.find((location) => {
        return (
          location.names[0]?.shortName ===
          decodeURIComponent(smartracksLocationName ?? "")
        );
      });
    }
    return undefined;
  }, [smartracksLocationName, sportsLocations]);

  const handleViewportChange = (event: ViewStateChangeEvent) => {
    setLatLngZoom({
      latitude: event.viewState.latitude,
      longitude: event.viewState.longitude,
      zoom: event.viewState.zoom,
    });
    setBounds(event.target.getBounds());
  };

  const handleClick = (event: MapLayerMouseEvent) => {
    const feature =
      event.features !== undefined ? event.features[0] : undefined;

    if (feature !== undefined) {
      const clusterId = feature.properties?.cluster_id ?? "";
      const featureSource = feature.source;

      if (clusterId !== "" && featureSource) {
        const source = mapRef.current?.getSource(
          featureSource,
        ) as GeoJSONSource;

        if (source !== null) {
          source.getClusterExpansionZoom(clusterId, (err, zoom) => {
            if (err) return;

            const point = feature.geometry as Point;
            if (point)
              mapRef.current?.flyTo({
                center: [point.coordinates[0], point.coordinates[1]],
                zoom: (zoom ?? defaultLatLngZoom.zoom) + 1,
                duration: 500,
                essential: true,
              });
          });
        }
      } else {
        if (feature !== undefined && !feature.properties?.cluster_id) {
          const locationName =
            JSON.parse(feature.properties?.names)[0]?.shortName ?? "";
          if (locationName !== "")
            setSmartracksLocationName(encodeURIComponent(locationName));
          handleViewLocation(feature.properties as SportsLocation);
        }
      }
    }
  };

  const popup = new mapboxgl.Popup({
    closeButton: false,
    closeOnClick: false,
    offset: 36,
    className: "myMapboxgl-popup",
  });

  const handlePopupMouseEnter = (e: mapboxgl.MapMouseEvent) => {
    if (e.features === undefined) return;
    const popups = document.getElementsByClassName("mapboxgl-popup");
    if (popups.length) return; // We can always have just one popup

    const map = e.target;

    // Change the cursor style as a UI indicator.
    map.getCanvas().style.cursor = "pointer";
    // Copy coordinates array.
    const coordinates = (e.features[0].geometry as Point).coordinates.slice();
    const description = JSON.parse(e.features[0].properties?.names)[0]
      .shortName;

    // Ensure that if the map is zoomed out such that multiple
    // copies of the feature are visible, the popup appears
    // over the copy being pointed to.
    if (["mercator", "equirectangular"].includes(map.getProjection().name)) {
      while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
        coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
      }
    }

    // Populate the popup and set its coordinates
    // based on the feature found.
    popup
      .setLngLat([coordinates[0], coordinates[1]])
      .setHTML(description)
      .addTo(map);
  };

  const handlePopupMouseLeave = (e: mapboxgl.MapMouseEvent) => {
    const map = e.target;
    map.getCanvas().style.cursor = "";
    popup.remove();
  };

  const handleMapLoad = () => {
    mapRef.current?.loadImage(`/img/smartracks-pin.png`, (err, img) => {
      if (err || img === undefined || img === null) return;
      if (!mapRef.current?.hasImage("smartracks-marker"))
        mapRef.current?.addImage("smartracks-marker", img);
    });

    mapRef.current?.loadImage(`/img/non-smartracks-pin.png`, (err, img) => {
      if (err || img === undefined || img === null) return;
      if (!mapRef.current?.hasImage("non-smartracks-marker"))
        mapRef.current?.addImage("non-smartracks-marker", img);
    });
    mapRef.current?.loadImage(
      `/img/smartracks-cluster-symbol.png`,
      (err, img) => {
        if (err || img === undefined || img === null) return;
        if (!mapRef.current?.hasImage("smartracks-cluster-symbol"))
          mapRef.current?.addImage("smartracks-cluster-symbol", img);
      },
    );
    mapRef.current?.loadImage(
      `/img/non-smartracks-cluster-symbol.png`,
      (err, img) => {
        if (err || img === undefined || img === null) return;
        if (!mapRef.current?.hasImage("non-smartracks-cluster-symbol"))
          mapRef.current?.addImage("non-smartracks-cluster-symbol", img);
      },
    );
    mapRef.current?.loadImage(`/img/mixed-cluster-symbol.png`, (err, img) => {
      if (err || img === undefined || img === null) return;
      if (!mapRef.current?.hasImage("mixed-cluster-symbol"))
        mapRef.current?.addImage("mixed-cluster-symbol", img);
    });
    /**
     * this is necessary since some web-browsers of some devices has UI covers
     * part of screen, so the map will be cropped and some elemnts will be covered under the UI
     * in this way we alway calculating per convention that the screen UI will always take 1% of vh and resizing our Map to this
     */

    // To only get popups on unclustered pins, we need to scope the
    // `on` calls to the layer id, otherwise clusters would get a popup
    mapRef.current
      ?.getMap()
      .on("mouseenter", "unclustered-point-st", handlePopupMouseEnter);
    mapRef.current
      ?.getMap()
      .on("mouseleave", "unclustered-point-st", handlePopupMouseLeave);
  };

  const smarTracksLocationsGeoJson = React.useMemo(
    (): FeatureCollection<Point, SportsLocation> => ({
      type: "FeatureCollection",
      features: (sportsLocations ?? []).map((location) => ({
        type: "Feature",
        geometry: {
          type: "Point",
          coordinates: [
            location.anchorLocation.longitude,
            location.anchorLocation.latitude,
          ],
        },
        properties: { ...location },
      })),
    }),
    [sportsLocations],
  );

  const handleRecenter = () => {
    mapRef.current?.flyTo({
      center: [defaultLatLngZoom.longitude, defaultLatLngZoom.latitude],
      zoom: defaultLatLngZoom.zoom,
      duration: 1000,
      essential: true,
    });
  };

  /**
   * Implements a hover effect when the mouse is moved over cluster features
   */
  const handleMouseMove = (e: mapboxgl.MapMouseEvent) => {
    const map = e.target;
    if (hoveredFeatureId)
      map.setFeatureState(
        { id: hoveredFeatureId, source: TRACKS_LAYER_ID },
        { hover: false },
      );

    if (
      e.features &&
      e.features.length > 0 &&
      e.features[0].layer?.id === tracksClusterLayer.id
    ) {
      map.setFeatureState(e.features[0], { hover: true });
      setHoveredFeatureId(e.features[0].id);
    } else setHoveredFeatureId(undefined);
  };

  const handleMouseLeave = (e: mapboxgl.MapMouseEvent) => {
    const map = e.target;
    if (hoveredFeatureId) {
      map.setFeatureState(
        { id: hoveredFeatureId, source: TRACKS_LAYER_ID },
        { hover: false },
      );
      setHoveredFeatureId(undefined);
    }
  };
  const mobileScreen = useMediaQuery(theme.breakpoints.down("md"));
  const mapCustomControls = (ref: Ref<mapboxgl.GeolocateControl>) => (
    <>
      <ScaleControl unit="metric" />
      <NavigationControl showCompass={false} position="bottom-right" />
      <GeolocateControl
        ref={ref}
        positionOptions={{ enableHighAccuracy: true }}
        trackUserLocation={true}
        position="bottom-right"
      />
    </>
  );

  const windowHeight = window.innerHeight;
  const windowWidth = window.innerWidth;

  return (
    <ReactMap
      mapboxAccessToken={MAPBOX_TOKEN}
      ref={mapRef}
      style={{
        width: `${windowWidth}px`,
        height: `${windowHeight}px`,
      }}
      attributionControl={false}
      customAttribution={ATTRIBUTION}
      mapStyle={`mapbox://styles/mapbox/${mapStyle}`}
      interactiveLayerIds={[
        tracksClusterLayer.id ?? "",
        tracksUnclusteredPointsLayer.id ?? "",
      ]}
      minZoom={0}
      latitude={latLngZoom.latitude}
      longitude={latLngZoom.longitude}
      zoom={latLngZoom.zoom}
      onLoad={() => {
        handleMapLoad();
        setMapLoaded(true);
        setBounds(mapRef.current?.getBounds() ?? null);
      }}
      onMove={handleViewportChange}
      onClick={handleClick}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onRender={() => mapRef.current?.resize()}
      onStyleData={handleMapLoad}
    >
      {/** Built-in controls */}
      {!mobileScreen && (
        /**
         * since the default Attribution positioned bottom right making it hard
         * to marign and padding other elements and cropping other elements if its
         * opened, this custom Element is necessary
         */
        <>
          <AttributionControl customAttribution={ATTRIBUTION} />
          <FullscreenControl position="bottom-right" />
          {mapCustomControls(geolocateRef)}
        </>
      )}
      {/** Box for custom controls */}
      <Box
        sx={{
          zIndex: 7,
          position: "absolute",
          right: 0,
          bottom: [document.fullscreenEnabled ? 155 : 114, 0],
        }}
      >
        <GroupToggle
          group={groupPins ?? true}
          onClick={(isGrouped) => setGroupPins(isGrouped ? undefined : false)}
        />
        <MapRecenter label="Recenter" onClick={handleRecenter} />
      </Box>
      {mobileScreen && (
        <>
          <Box sx={{ zIndex: 11 }}>
            <AttributionControl
              position="bottom-left"
              style={{
                zIndex: 3,
                bottom: -30,
                left: -4,
              }}
              customAttribution={ATTRIBUTION}
            />
          </Box>
          <FullscreenControl position="bottom-right" />
          {mapCustomControls(geolocateRef)}
        </>
      )}
      {mapLoaded && visibleLocations && (
        <SportsLocationOverlayLayer
          visibleAtZoom={MIN_SVG_ZOOM_LEVEL}
          currentMapZoom={latLngZoom.zoom}
          sportsLocations={
            visibleLocations ? visibleLocations : new Array<SportsLocation>()
          }
        />
      )}
      {mapLoaded && sportsLocations && groupPins && (
        <Source
          id={TRACKS_LAYER_ID}
          type="geojson"
          data={smarTracksLocationsGeoJson}
          cluster={true}
          clusterMaxZoom={MIN_SVG_ZOOM_LEVEL - 1}
          clusterRadius={64}
          clusterProperties={{
            smartracksEnabledCount: [
              "+",
              ["case", ["==", ["get", "smarTracksEnabled"], true], 1, 0],
            ],
          }}
        >
          <Layer {...tracksClusterLayer} />
          <Layer {...tracksUnclusteredPointsLayer} />
        </Source>
      )}
      {mapLoaded && sportsLocations && !groupPins && (
        <Source
          id={TRACKS_LAYER_ID}
          type="geojson"
          data={smarTracksLocationsGeoJson}
          cluster={false}
        >
          <Layer {...tracksClusterLayer} />
          <Layer {...tracksUnclusteredPointsLayer} />
        </Source>
      )}
      {bounds &&
        sportsLocations &&
        latLngZoom.zoom > MIN_SVG_ZOOM_LEVEL &&
        smarTracksLocationsGeoJson.features
          .filter((f) => {
            // Hide markers that are not in the current view bounds
            const point = f.geometry;
            return (
              point.coordinates[1] >= bounds.getSouthWest().lat &&
              point.coordinates[1] <= bounds.getNorthWest().lat &&
              point.coordinates[0] >= bounds.getSouthWest().lng &&
              point.coordinates[0] <= bounds.getNorthEast().lng
            );
          })
          .map((e) => (
            <Box
              key={e.properties.sportsLocationGUID}
              sx={{ zIndex: "5 !important" }}
            >
              <Marker
                longitude={e.geometry.coordinates[0]}
                latitude={e.geometry.coordinates[1]}
                onClick={() => handleClick}
                style={{
                  zIndex: 5,
                  width: "23.25px /* 75% of the original png */",
                  height: "34.5px /* 75% of the original png */",
                }}
                anchor="bottom"
              >
                <img
                  alt="smartracks-pin"
                  style={{ width: "100%", height: "100%" }}
                  src={
                    e.properties.smarTracksEnabled
                      ? `/img/smartracks-pin.png`
                      : `/img/non-smartracks-pin.png`
                  }
                />
              </Marker>
            </Box>
          ))}
      {LocationInfoData && smartracksLocationName && (
        <Box>
          <SmartracksLocationInfo
            sportsLocation={LocationInfoData}
            onRegionSelected={onRegionSelected}
          />
        </Box>
      )}
    </ReactMap>
  );
};

export default Map;
